import React from 'react';
import type { EmbeddedPodletResponse } from './Podlets';

const podletRegistry = new WeakSet();
const isServer = typeof window === 'undefined';

export function usePodletContent(response: EmbeddedPodletResponse) {
    const podletRef = React.useRef<HTMLDivElement | null>(null);
    const hasRerenderedRef = React.useRef(false);

    const isHydrating = checkHydration(response);
    const hasShadowRoot = checkShadowRoot(response, podletRef);

    const isHydrationErrorRerendering =
        !isServer && !isHydrating && !hasShadowRoot;

    if (isHydrationErrorRerendering) {
        hasRerenderedRef.current = true;
    }

    const hasClientSideRerendered = !isServer && hasRerenderedRef.current;

    const innerHTML = React.useMemo(() => {
        return {
            __html:
                isServer ||
                isHydrationErrorRerendering ||
                hasClientSideRerendered
                    ? response.content
                    : removeIsolatedPodletDsdTemplate(response.content),
        };
    }, [
        hasClientSideRerendered,
        isHydrationErrorRerendering,
        response.content,
    ]);

    return {
        innerHTML,
        isHydrationErrorRerendering,
        setPodletRef: (element: HTMLDivElement | null) => {
            podletRef.current = element;
        },
    };
}

function checkHydration(response: EmbeddedPodletResponse): boolean {
    const isHydrating = !podletRegistry.has(response);
    podletRegistry.add(response);
    return isHydrating;
}

function checkShadowRoot(
    response: EmbeddedPodletResponse,
    podletRef: React.MutableRefObject<HTMLDivElement | null>,
): boolean {
    const podletName = response.content.match(
        /<([\w-]+-isolated)\b[^>]*>/,
    )?.[1];
    const shadowRoot =
        podletName &&
        !!podletRef.current?.querySelector(podletName)?.shadowRoot;
    return Boolean(shadowRoot);
}

function removeIsolatedPodletDsdTemplate(html: string) {
    const regex =
        /(<[\w-]+-isolated\b[^>]*>.*?)<template\s+shadowrootmode[^>]*>.*?<\/template>(.*?<\/[\w-]+-isolated>)/gis;
    return html.replace(regex, '$1$2');
}
